import type { DataCategory } from "./automations"

export type CategoryField = { id: string; name: string; type: "text" | "number" | "date" | "boolean" | "enum"; unit: string; description: string; required: boolean; choices: string[] }
export type CategoryDefinition = { id: string; name: string; description: string; fields: CategoryField[] }
export type CategoryDefinitions = { revision: number; categories: CategoryDefinition[] }

export function workflowCategories(definitions: CategoryDefinition[] | undefined, applied: DataCategory[]): DataCategory[] {
  if (!definitions) return applied
  return [...definitions.map((category) => ({ ...category, id: `definition:${category.id}`, key: category.id,
    inferred: false, source_file_ids: [], extraction_id: "", workflow_run_id: null, status: "defined",
  })), ...applied.filter((category) => category.status !== "defined")]
}

export function categoryFromApplied(category: DataCategory): CategoryDefinition {
  return { id: crypto.randomUUID(), name: category.name, description: category.description,
    fields: category.fields.map((field) => ({ id: crypto.randomUUID(), name: field.name, description: field.description,
      type: (["text", "number", "date", "boolean", "enum"].includes(field.type) ? field.type : "text") as CategoryField["type"],
      unit: field.unit ?? "", required: field.required, choices: [...(field.choices ?? [])],
    })),
  }
}

// Adapted from leshabank's workspace-categories authoring helpers.
export function categoryValidation(category: CategoryDefinition, definitions: CategoryDefinition[]) {
  if (!category.name.trim()) return "name" as const
  if (definitions.some((item) => item.id !== category.id && item.name.trim().toLowerCase() === category.name.trim().toLowerCase())) return "duplicateCategory" as const
  if (definitions.length >= 100 && !definitions.some((item) => item.id === category.id)) return "limit" as const
  if (!category.fields.length || category.fields.length > 200) return "fields" as const
  if (category.fields.some((field) => !field.name.trim())) return "fieldName" as const
  if (new Set(category.fields.map((field) => field.name.trim().toLowerCase())).size !== category.fields.length) return "duplicateField" as const
  if (category.fields.some((field) => field.type === "enum" && (!field.choices.length || field.choices.length > 200 || field.choices.some((choice) => !choice.trim() || choice.length > 255)))) return "choices" as const
}

export function categorySaveBody(definitions: CategoryDefinitions, category: CategoryDefinition): CategoryDefinitions {
  const saved = { ...category, name: category.name.trim(), description: category.description.trim(), fields: category.fields.map((field) => ({
    ...field, name: field.name.trim(), description: field.description.trim(), unit: field.unit.trim(),
    choices: field.type === "enum" ? [...new Set(field.choices.map((choice) => choice.trim()).filter(Boolean))] : [],
  })) }
  return { revision: definitions.revision, categories: [
    ...definitions.categories.map((item) => item.id === saved.id ? saved : item),
    ...(definitions.categories.some((item) => item.id === saved.id) ? [] : [saved]),
  ] }
}
